<script setup lang="ts">
    import { useRouter, useRoute } from "vue-router"
    import type { Router, RouteRecordRaw } from "vue-router"
    import { useUserStore } from "@/stores/user"
    import { computed, ref, provide } from "vue"

    import checkPermissions from "@/utils/checkPermissions"

    const router = useRouter()
    const route = useRoute()
    const userStore = useUserStore()
    const isCollapse = ref(false)

    const { hasPermissionTopNavbarItem, currentTopNavbarItem } =
        useTopNavbarItem(router, userStore)

    const { firstSidebarItems } = useFirstSidebarItems(
        hasPermissionTopNavbarItem,
        userStore
    )
    const { sideNavbarItems, currenSideNavbarItem } = useSideNavbarItem(
        currentTopNavbarItem,
        userStore
    )

    const currentContainerPadding = computed(() => {
        if (sideNavbarItems.value.length) {
            return isCollapse.value ? "58px 0px 0px 62px" : "58px 0px 0px 199px"
        }
        return "58px 16px 0px"
    })

    const sidebarWidth = computed(() => {
        if (sideNavbarItems.value.length)
            return isCollapse.value ? "62px" : "199px"
        return "0px"
    })

    provide("sidebarWidth", sidebarWidth)

    type MenuItem = {
        name?: string
        path?: string
        meta: { position: string; index: string; name: string; icon?: string }
    }
    type SubMenuItem = { name?: string }
    const menuItems = computed(
        () => hasPermissionTopNavbarItem.value as unknown as MenuItem[]
    )
    const subMenuItems = computed(
        () => firstSidebarItems.value as unknown as SubMenuItem[][]
    )

    function useTopNavbarItem(
        router: Router,
        userStore: ReturnType<typeof useUserStore>
    ) {
        const hasPermissionTopNavbarItem = computed(() => {
            return router.options.routes
                .filter((item) => item.meta?.isShow)
                .filter((item) => {
                    if (item.meta?.permissions) {
                        return checkPermissions(
                            item.meta.permissions,
                            userStore
                        )
                    } else {
                        const permissions = (item.children ?? [])
                            .filter((item) => item.meta?.permissions)
                            .flatMap((item) => item.meta?.permissions ?? [])
                        return checkPermissions(permissions, userStore)
                    }
                })
                .slice()
                .sort((a, b) => (a.meta?.index ?? 0) - (b.meta?.index ?? 0))
        })

        const currentTopNavbarItem = computed(() => {
            return (
                hasPermissionTopNavbarItem.value.filter((item) => {
                    return (
                        item.meta?.menuGroup ===
                        router.currentRoute.value.meta?.menuGroup
                    )
                })[0] || {}
            )
        })

        return { hasPermissionTopNavbarItem, currentTopNavbarItem }
    }

    function useFirstSidebarItems(
        hasPermissionTopNavbarItem: { value: RouteRecordRaw[] },
        userStore: ReturnType<typeof useUserStore>
    ) {
        const firstSidebarItems = computed(() => {
            return hasPermissionTopNavbarItem.value.map((topItem) => {
                return topItem?.children
                    ?.filter((item) => item.meta?.isSubShow)
                    .filter((item) => {
                        return checkPermissions(
                            item.meta?.permissions,
                            userStore
                        )
                    })
                    .slice()
                    .sort(
                        (a, b) =>
                            (a.meta?.subIndex ?? 0) - (b.meta?.subIndex ?? 0)
                    )
            })
        })

        return {
            firstSidebarItems,
        }
    }

    function useSideNavbarItem(
        currentTopNavbarItem: { value: { children?: RouteRecordRaw[] } },
        userStore: ReturnType<typeof useUserStore>
    ) {
        const sideNavbarItems = computed(() => {
            let currentMenu = currentTopNavbarItem.value

            // 無當前頂部導航欄的項目使用判斷獲得側邊導航欄項目
            const menuGroups = [
                "account-manage",
                "notification-center",
                "platform-settings",
            ]
            if (menuGroups.includes(route.meta.menuGroup ?? "")) {
                currentMenu = router.options.routes.filter(
                    (item) => item.meta?.menuGroup === route.meta.menuGroup
                )[0]
            }

            return (
                currentMenu?.children
                    ?.filter((item) => item.meta?.isSubShow)
                    .filter((item) => {
                        return checkPermissions(
                            item.meta?.permissions,
                            userStore
                        )
                    })
                    .slice()
                    .sort(
                        (a, b) =>
                            (a.meta?.subIndex ?? 0) - (b.meta?.subIndex ?? 0)
                    ) || []
            )
        })

        const currenSideNavbarItem = computed(() => {
            return route
        })

        return { sideNavbarItems, currenSideNavbarItem }
    }
</script>

<template>
    <el-container>
        <el-header style="padding: 0" class="default-header">
            <BaseMenu
                :items="menuItems"
                :current-item="currentTopNavbarItem"
                :subitems="subMenuItems"
                :user="userStore.userInfo!"
            />
        </el-header>
        <el-container>
            <el-aside v-if="sideNavbarItems.length" class="default-aside">
                <BaseNavbar
                    v-model:is-collapse="isCollapse"
                    :items="sideNavbarItems"
                    :current-item="currenSideNavbarItem"
                />
            </el-aside>
            <el-main style="background-color: #fafcff" class="default-main">
                <router-view />
            </el-main>
        </el-container>
    </el-container>
</template>

<style scoped lang="scss">
    .default-header {
        position: fixed;
        z-index: 99;
        left: 0;
        right: 0;
    }

    .default-aside {
        position: fixed;
        z-index: 0;
        top: 60px;
        max-width: 200px;
        transition: all 0.3s ease;
    }

    .default-main {
        padding: v-bind("currentContainerPadding");
        min-height: 100vh;
        transition: padding-left 0.3s ease;
    }
</style>
